import { useState, useEffect, FC, useCallback } from "react";
import { Input, InputProps } from "antd";

/**
 * Props interface for the CurrencyInput component
 */
interface CurrencyInputProps extends Omit<InputProps, "value" | "onChange"> {
  /** The value of the input as a number */
  value?: number;
  /** Callback fired when the value changes */
  onChange?: (value: number | undefined) => void;
  /** Currency symbol or prefix to display before the value (default: '€') */
  prefix?: string;
  /** Placeholder text when input is empty (default: '0,00') */
  placeholder?: string;
  /** Whether to allow negative values (default: false) */
  allowNegative?: boolean;
  /** Minimum value allowed */
  min?: number;
  /** Step value for increments */
  step?: number;
}

/**
 * CurrencyInput - A React component for currency input fields with format
 *
 * Features:
 * - Currency format (e.g., € 1.234,56)
 * - Configurable currency symbol/prefix
 * - Compatible with Ant Design v4
 * - Works with Ant Design Form
 * - Supports negative values (optional)
 */
const CurrencyInput: FC<CurrencyInputProps> = ({
  value,
  onChange,
  prefix = "€",
  placeholder = "0,00",
  allowNegative = false,
  min,
  step,
  ...props
}) => {
  // State for the displayed value
  const [displayValue, setDisplayValue] = useState<string>("");
  // Track if the field is being edited to prevent format interference
  const [isEditing, setIsEditing] = useState<boolean>(false);

  /**
   * Parses a formatted currency string to a number
   */
  const parseCurrency = useCallback(
    (formattedValue: string): number | undefined => {
      if (!formattedValue || typeof formattedValue !== "string")
        return undefined;

      // Check for negative sign
      const isNegative = formattedValue.includes("-");
      if (isNegative && !allowNegative) return undefined;

      // Remove everything except digits, dots, commas, and minus sign
      let cleanValue = formattedValue.replace(/[^\d,.-]/g, "");

      // Handle negative sign
      if (isNegative) {
        cleanValue = "-" + cleanValue.replace(/-/g, "");
      } else {
        cleanValue = cleanValue.replace(/-/g, "");
      }

      // Handle number format (1.234,56) - dots are thousand separators, comma is decimal separator
      // Replace all dots and convert comma to dot for JavaScript parsing
      cleanValue = cleanValue.replace(/\./g, "").replace(/,/g, ".");

      // If multiple decimal points remain after replacement, keep only the first one
      const parts = cleanValue.split(".");
      if (parts.length > 2) {
        cleanValue = parts[0] + "." + parts.slice(1).join("");
      }

      const result = parseFloat(cleanValue);

      if (isNaN(result)) return undefined;

      // Apply min constraint if specified
      if (min !== undefined && result < min) return min;

      return result;
    },
    [allowNegative, min]
  );

  /**
   * Formats a number to currency format (e.g., 1.234,56)
   */
  const formatCurrency = useCallback(
    (value?: number | string | null): string => {
      if (value === undefined || value === null || value === "") return "";

      let numValue: number;
      if (typeof value !== "number") {
        const parsed = parseCurrency(String(value));
        if (parsed === undefined) return "";
        numValue = parsed;
      } else {
        numValue = value;
      }

      // Format using our shared currencyFormatter but strip the € symbol
      return numValue.toLocaleString("de-DE", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    [parseCurrency]
  );

  // Update display value when external value changes or editing state changes
  useEffect(() => {
    if (!isEditing) {
      const formatted = formatCurrency(value);
      setDisplayValue(formatted);
    }
  }, [value, isEditing, formatCurrency]);

  /**
   * Handle input changes
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Handle empty input
    if (!inputValue) {
      setDisplayValue("");
      if (onChange) onChange(undefined);
      return;
    }

    // Remove prefix for processing if it exists
    const valueWithoutPrefix = inputValue.startsWith(prefix)
      ? inputValue.substring(prefix.length).trim()
      : inputValue;

    // Validate input characters (allow digits, commas, dots, spaces, and minus if enabled)
    const validPattern = allowNegative ? /^[-\d\s,.]*$/ : /^[\d\s,.]*$/;
    if (!validPattern.test(valueWithoutPrefix)) {
      return;
    }

    setDisplayValue(valueWithoutPrefix);

    // Parse and update only if valid
    const numericValue = parseCurrency(valueWithoutPrefix);
    if (numericValue !== undefined && onChange) {
      onChange(numericValue);
    }
  };

  /**
   * Handle blur event - format value and disable editing mode
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);

    if (!displayValue) {
      setDisplayValue("");
      if (onChange) onChange(undefined);
    } else {
      const numericValue = parseCurrency(displayValue);
      if (numericValue !== undefined) {
        const formatted = formatCurrency(numericValue);
        setDisplayValue(formatted);
        if (onChange) onChange(numericValue);
      }
    }

    if (props.onBlur) {
      props.onBlur(e);
    }
  };

  // Remove handlers we manage internally
  const { onFocus, onBlur, ...restProps } = props;

  return (
    <Input
      {...restProps}
      placeholder={placeholder}
      prefix={prefix}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    />
  );
};

export default CurrencyInput;
